import { createRouter, createWebHistory } from 'vue-router';
import Dashboard from '@/views/dashboard';
import Floorplans from '@/views/floorplans';
import MarketHomes from '@/views/market_homes';
import Communities from '@/views/communities';
import Collections from '@/views/collections';
import Agents from '@/views/agents';
import Locations from '@/views/locations';
import Media from '@/views/media';
import Users from '@/views/users';
import Surveys from '@/views/surveys';
import Promotions from '@/views/promotions';
import Map from '@/views/map';
import Error404 from '@/views/404.vue';
import { useAuthStore } from '@/stores/auth';

import axios from 'axios';
import { useMessageStore } from './stores/message';
import LoginPage from './views/LoginPage.vue';
import Reports from './views/reports';
axios.defaults.baseURL = `${import.meta.env.VITE_APP_ENDPOINT}api/`;
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

const routes = [
    {
      path: '/',
      component: Dashboard.ListView,
      name: 'dashboard',
      meta: {
        icon: 'fa-dashboard',
        menu: true,
        title: 'Dashboard',
        order: 1,
        acl: 0
      }
    },
    {
      path: '/login',
      component: LoginPage,
      name: 'login',
      meta: {
        menu: false,
        title: 'Login',
        acl: -1
      }
    },
    {
      path: '/map',
      children: [
        {
          path: '',
          component: Map.Map,
          name: 'map',
          meta: {
            icon: 'fa-map',
            menu: true,
            title: 'Map',
            order: 1.1,
            acl: -1
          },
          props: () => ({ community: null })
        },
        {
          path: 'community/:community',
          children: [
            {
              path: '',
              component: Map.Map,
              name: 'map-community',
              meta: {
                menu: false,
                title: 'Community Map',
                acl: -1
              },
              props: route => ({ community: route.params?.community * 1 }),
            },
            {
              path: 'collections/:collections',
              component: Map.Map,
              name: 'map-collections',
              meta: {
                menu: false,
                title: 'Community Collections Map',
                acl: -1
              },
              props: route => ({ community: route.params.community * 1, collection_ids: route.params.collections.split(',').map(collection => collection * 1) })
            }
          ]
        }
      ]
    },
    {
      path: '/available-homes/',
      component: MarketHomes.ListView,
      name: 'available-homes-list',
      meta: {
        icon: 'fa-home-available',
        menu: true,
        title: 'Available Homes',
        order: 4,
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/available-homes/:id',
      component: MarketHomes.ShowView, 
      name: 'available-homes-show',
      meta: {
        title: 'Available Home',
        parent: 'available-homes-list',
        acl: 0,
        background: 'white'
      },
      children: [
        {
          path: ':tab',
          name: 'available-homes-tab', 
          component: MarketHomes.ShowView
        }
      ]
    },
    {
      path: '/media/',
      component: Media.ListView,
      name: 'media-list',
      meta: {
        icon: 'fa-photo',
        title: 'Media Library',
        menu: true,
        order: 5,
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/media/:id',
      component: Media.ShowView,
      name: 'media-show',
      meta: {
        parent: 'media-list',
        title: 'Media Item Details',
        acl: 0
      },
      children: [
        {
          path: ':tab',
          name: 'media-tab', 
          component: Media.ShowView
        }
      ]
    },
    {
      path: '/media/edit/:id',
      component: Media.ShowView,
      name: 'media-edit',
      meta: {
        parent: 'media-list',
        title: 'Edit Media Item',
        acl: 1
      }
    },
    {
      path: '/floorplans',
      component: Floorplans.ListView,
      name: 'floorplan-list',
      meta: {
        icon: 'fa-floorplan',
        title: 'Floorplans',
        menu: true,
        order: 3,
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/floorplans/:id',
      component: Floorplans.ShowView,
      name: 'floorplan-show',
      meta: {
        parent: 'floorplan-list',
        title: 'Floorplan',
        acl: 0,
        background: 'white'
      },
      children: [
        {
          path: ':tab',
          name: 'floorplan-tab', 
          component: Floorplans.ShowView
        }
      ]
    },
    {
      path: '/communities',
      component: Communities.ListView,
      name: 'community-list',
      meta: {
        icon: 'fa-community',
        title: 'Communities',
        menu: true,
        order: 2,
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/communities/:id',
      component: Communities.ShowView,
      name: 'community-show',
      meta: {
        parent: 'community-list',
        title: 'Community',
        acl: 0,
        background: 'white'
      },
      children: [
        {
          path: ':tab',
          name: 'community-tab', 
          component: Communities.ShowView
        }
      ]
    },
    {
      path: '/collections',
      component: Collections.ListView,
      name: 'collection-list',
      meta: {
        icon: 'fa-collection',
        title: 'Collections',
        menu: true,
        order: 2.1,
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/collections/:id',
      component: Collections.ShowView,
      name: 'collection-show',
      meta: {
        parent: 'collection-list',
        title: 'Collection',
        acl: 0,
        background: 'white'
      },
      children: [
        {
          path: ':tab',
          name: 'collection-tab', 
          component: Collections.ShowView
        }
      ]
    },
    {
      path: '/users',
      component: Users.ListView,
      name: 'user-list',
      meta: {
        icon: 'fa-users',
        title: 'Users',
        menu: true,
        order: 6,
        acl: 2,
        background: 'white'
      }
    },
    {
      path: '/users/add',
      component: Users.EditView,
      name: 'user-add',
      meta: {
        title: 'Add New User',
        parent: 'user-list',
        acl: 2,
        background: 'white'
      }
    },
    {
      path: '/users/:id/edit',
      component: Users.ShowView,
      name: 'user-edit',
      meta: {
        title: 'User',
        parent: 'user-list',
        acl: 2,
        background: 'white'
      }
    },
    {
      path: '/profile/:tab',
      component: Users.ShowView,
      name: 'profile-tab',
      meta: {
        title: 'Profile',
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/locations',
      component: Locations.ListView,
      name: 'location-list',
      meta: {
        icon: 'fa-location',
        title: 'Locations',
        menu: true,
        order: 6.1,
        acl: 2,
        background: 'white'
      }
    },
    {
      path: '/agents',
      redirect: '/team'
    },
    {
      path: '/agents/add',
      redirect: '/team/add'
    },
    {
      path: '/agents/:id/edit',
      redirect: to => `/team/${to.params.id}/edit`
    },
    {
      path: '/agents/:id',
      redirect: to => `/team/${to.params.id}`
    },
    {
      path: '/team',
      component: Agents.ListView,
      name: 'agent-list',
      meta: {
        icon: 'fa-sales-team',
        title: 'Sales Team',
        menu: true,
        order: 5.1,
        acl: 0,
        background: 'white'
      }
    },
    {
      path: '/team/:id',
      component: Agents.ShowView,
      name: 'agent-show',
      meta: {
        title: 'View Sales Team Member',
        parent: 'agent-list',
        acl: 1,
        background: 'white'
      },
      children: [
        {
          path: ':tab',
          name: 'agent-tab', 
          component: Agents.ShowView
        }
      ]
    },
    {
      path: '/team/:id/edit',
      component: Agents.EditView,
      name: 'agent-edit',
      meta: {
        title: 'Edit Sales Team Member',
        parent: 'agent-list',
        acl: 1
      }
    },
    {
      path: '/team/add',
      component: Agents.EditView,
      name: 'agent-add',
      meta: {
        title: 'Add New Sales Team Member',
        parent: 'agent-list',
        acl: 1
      }
    },
    {
      path: '/surveys',
      component: Surveys.ListView,
      name: 'survey-list',
      meta: {
        icon: 'fa-survey',
        title: 'Surveys',
        menu: true,
        order: 5.2,
        acl: 2,
        background: 'white'
      }
    },
    {
      path: '/surveys/:id',
      component: Surveys.ShowView,
      name: 'survey-show',
      meta: {
        title: 'Survey',
        parent: 'survey-list',
        acl: 2,
        background: 'white'
      },
      children: [
        {
          path: ':tab',
          name: 'survey-tab', 
          component: Surveys.ShowView
        }
      ]
    },
    {
      path: '/surveys/add',
      component: Surveys.AddView,
      name: 'survey-add',
      meta: {
        title: 'Add New Survey',
        parent: 'survey-list',
        acl: 1
      }
    },
    {
      path: '/promotions',
      component: Promotions.ListView,
      name: 'promotion-list',
      meta: {
        icon: 'fa-dollar',
        title: 'Promotions',
        menu: true,
        order: 5.3,
        acl: 2,
        background: 'white'
      }
    },
    {
      path: '/promotions/add',
      component: Promotions.AddView,
      name: 'promotion-add',
      meta: {
        title: 'Add New Promotion',
        parent: 'promotion-list',
        acl: 2
      }
    },
    {
      path: '/promotions/:id',
      component: Promotions.ShowView,
      name: 'promotion-show',
      meta: {
        parent: 'promotion-list',
        title: 'Promotion Details',
        acl: 2
      },
      children: [
        {
          path: ':tab',
          name: 'promotion-tab',
          component: Promotions.ShowView
        }
      ]
    },
    {
      path: '/parcels/:id',
      name: 'parcel-show'
    },
    {
      path: '/:pathMatch(.*)*',
      name: 404,
      component: Error404,
      meta: {
        title: 'Page Not Found'
      }
    },
    // {
    //   path: '/reports',
    //   name: 'reports',
    //   component: Reports.ListView,
    //   meta: {
    //     title: 'Reports',
    //     icon: 'fa-table',
    //     acl: 2,
    //     menu: true,
    //     order: 7
    //   }
    // }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return new Promise((resolve) => {
      resolve({ top: 0, left: 0 });
    })
  }
});

router.beforeEach(async (to) => {
  const store = useAuthStore(),
  message = useMessageStore();
  message.clear();
  let allow = true;

  if((to.meta?.acl ?? 0) === -1) {
    // No authorization required for ACL permissions on this route
    return allow;
  }

  await store.check()
    .then(() => {
        if(store.user.level < to.meta.acl && to.name != 'dashboard') {
          allow = { name : 'dashboard' };
          message.setAlert({
            text: "You are not authorized to view that page",
            level: "error"
          })
        } else if(store.authenticated) {
          return;
        } else {
          allow = { 
            name: 'login'
          }
        }
  });

  if(allow !== true) return allow;
})

export default router;
